import {EnumValues, z} from 'zod';

type EnumType = readonly [string, ...string[]];
interface PossibleOptions {
  possibleQuestions: EnumType | [],
  possibleAnswers: EnumType | []
}

export function getBehaviorRulesSchema({
  possibleQuestions,
  possibleAnswers,
}: PossibleOptions) {
  const baseRuleSchema = z.object({
    operator: z.enum(['eq', 'ne', 'gt', 'lt', 'gte', 'lte']),
  });

  const ruleSchema = baseRuleSchema.extend({
    question: possibleQuestions.length ? z.enum(possibleQuestions as EnumValues) : z.string().min(1, 'Condition is required'),
    period: z.number().min(1, 'Period is required'),
    value: possibleAnswers.length ? z.enum(possibleAnswers as EnumValues) : z.string().min(1, 'Response is required'),
    // LF360-1810: task criteria carry these; kept optional/permissive since
    // ruleSchema is shared with message criteria which don't use them.
    isTaskManagement: z.boolean().optional(),
    taskWindow: z.enum(['today', 'yesterday', 'latest', 'last-n']).optional(),
    // LF360-1810 (task-window extension): 'last-n' only. threshold (K) is an
    // integer count; period (N) is left as-is here (not `.int()`) since the
    // same field is shared with message-response criteria - N is instead
    // clamped to an integer in the editor's input handler.
    matchMode: z.enum(['all', 'any', 'at-least']).optional(),
    threshold: z.number().int().min(1).optional(),
  });

  // LF360-1812: adherence criteria are subject-level - the metric is carried by
  // isAdherence + a numeric comparison operator + a percent threshold + rolling window.
  // question/value/period are NOT semantically used, but the editor seeds them
  // (question = the synthetic 'adherence' metric key). They MUST be declared here:
  // zodResolver strips unknown keys, and without `question` the criterion was
  // silently wiped on every form change -> lost on reload.
  const adherenceRuleSchema = z.object({
    isAdherence: z.literal(true),
    operator: z.enum(['eq', 'gt', 'lt', 'gte', 'lte']),
    threshold: z.number().int().min(0).max(100),
    windowDays: z.number().int().min(1),
    question: z.string().optional(),
    value: z.string().optional(),
    period: z.number().optional(),
  });

  // LF360-1828: participant-profile criteria are keyed by profileFieldId; the
  // metric is carried by isProfileField + fieldType (source field inputType,
  // drives the editor's operator/value control) + a generic operator/value pair.
  // question/period are NOT semantically used, but (same LF360-1812 pitfall as
  // adherenceRuleSchema above) the editor's ComboboxBehavior displays the
  // selected criterion via `criteria.question`, and BehaviorRuleStatementCurrent
  // requires both fields - they MUST be declared here (even optional) or
  // zodResolver strips them on every form change, silently blanking the
  // combobox and losing period on reload.
  const profileFieldRuleSchema = z.object({
    isProfileField: z.literal(true),
    profileFieldId: z.string().min(1, 'Profile field is required'),
    fieldType: z.string().min(1),
    operator: z.enum(['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'contains']),
    value: z.string().min(1, 'Value is required'),
    question: z.string().optional(),
    period: z.number().optional(),
  });

  // LF360-1828: onboarding-response criteria mirror profileFieldRuleSchema but
  // are keyed by onboardingQuestionId instead of profileFieldId. question/period
  // are NOT semantically used, but (same LF360-1812 pitfall as adherenceRuleSchema/
  // profileFieldRuleSchema above) the editor's ComboboxBehavior displays the
  // selected criterion via `criteria.question`, and BehaviorRuleStatementCurrent
  // requires both fields - they MUST be declared here (even optional) or
  // zodResolver strips them on every form change, silently blanking the combobox
  // and losing period on reload.
  const onboardingResponseRuleSchema = z.object({
    isOnboardingResponse: z.literal(true),
    onboardingQuestionId: z.string().min(1, 'Onboarding question is required'),
    fieldType: z.string().min(1),
    operator: z.enum(['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'contains']),
    value: z.string().min(1, 'Value is required'),
    question: z.string().optional(),
    period: z.number().optional(),
  });

// Define the rules schema
  const rulesSchema = z.object({
    operator: z.enum(['AND', 'OR']),
    criteria: z.array(z.union([adherenceRuleSchema, profileFieldRuleSchema, onboardingResponseRuleSchema, ruleSchema])).min(1, 'Some criteria should be selected'),
  });

  const ruleBasedConditionSchema = z.object({
    rules: rulesSchema,
  });

  const elseConditionSchema = z.object({
    type: z.literal('else'),
  });

// Define the condition schema
  const conditionSchema = z.union( [
    ruleBasedConditionSchema,
    elseConditionSchema,
  ]);

// Define the main schema
  return z.object({
    conditions: z.array(conditionSchema).min(1, 'Conditions are required'),
  });
}

// Define the base rule schema


// example data to validate
// const data = {
//   conditions: [
//     {
//       rules: {
//         operator: 'AND',
//         criteria: [
//           {
//             operator: 'eq',
//             question: 'question1',
//             value: 'value1',
//             period: 1,
//           },
//           {
//             operator: 'eq',
//             question: 'question2',
//             period: 5,
//             value: 'value2',
//           },
//         ],
//       },
//     },
//     {
//       type: 'else',
//     },
//   ],
// };
